import { usePuck, type PuckContext } from "@measured/puck";
import type { ComponentType, CSSProperties } from "react";
import type { LayoutDirection } from "@/lib/cms-puck/layout";
import { isPuckSlotEmpty } from "@/lib/cms-puck/puck-tree";

export type LayoutSlotVariant = "container" | "column" | "grid";

type SlotRenderProps = {
  minEmptyHeight?: number;
  className?: string;
  style?: CSSProperties;
};

const VARIANT_CONFIG: Record<
  LayoutSlotVariant,
  { minEmptyHeight: number; editorPadding: string; slotLayoutClass: string }
> = {
  container: {
    minEmptyHeight: 200,
    editorPadding: "p-3",
    slotLayoutClass: "",
  },
  // Colonnes : empilement sans gap par défaut (même logique que Container gap:none).
  column: {
    minEmptyHeight: 160,
    editorPadding: "p-0",
    slotLayoutClass: "flex w-full min-w-0 flex-col gap-0",
  },
  grid: {
    minEmptyHeight: 200,
    editorPadding: "p-3",
    slotLayoutClass: "",
  },
};

type LayoutSlotProps = {
  slot: ComponentType<SlotRenderProps>;
  slotKey: string;
  componentId: string;
  variant: LayoutSlotVariant;
  layoutClassName?: string;
  /** Direction du flex parent (Container uniquement). */
  layoutDirection?: LayoutDirection;
  /** Style inline structurel (ex. hauteur explicite du conteneur). */
  layoutStyle?: CSSProperties;
  /** Classes du conteneur cellule (Columns : alignement + largeur auto). */
  cellClassName?: string;
  puck?: PuckContext;
};

/**
 * Slot de mise en page — zone de drop Puck sans repères visuels. Réutilisé par
 * Container / Columns / Grid + les blocs à slot (TextImage…) à toute profondeur.
 *
 * `usePuck()` (hook ÉDITEUR) lève une erreur hors de `<Puck>` — c'est le cas de
 * l'APERÇU read-only rendu via `<Render>` (bibliothèque de modèles). On n'appelle
 * donc ce hook QUE dans la branche édition. `puck.isEditing` est fourni en prop
 * dans les deux contextes (false sous `<Render>`), ce qui permet de choisir la
 * bonne branche SANS appeler le hook. Sinon l'aperçu des blocs à slot planterait.
 */
export function LayoutSlot(props: LayoutSlotProps) {
  return props.puck?.isEditing ? (
    <LayoutSlotEditing {...props} />
  ) : (
    <LayoutSlotView {...props} />
  );
}

/** Branche APERÇU/rendu (hors éditeur) : aucun `usePuck()`, aucune classe d'édition. */
function LayoutSlotView(props: LayoutSlotProps) {
  return <LayoutSlotInner {...props} isEditing={false} isEmpty={false} />;
}

/** Branche ÉDITEUR : lit l'état Puck pour détecter le slot vide (repères visuels). */
function LayoutSlotEditing(props: LayoutSlotProps) {
  const { appState } = usePuck();
  const isEmpty = isPuckSlotEmpty(
    appState.data,
    props.componentId,
    props.slotKey,
  );
  return <LayoutSlotInner {...props} isEditing isEmpty={isEmpty} />;
}

function LayoutSlotInner({
  slot: Slot,
  variant,
  layoutClassName,
  layoutDirection,
  layoutStyle,
  cellClassName,
  isEditing,
  isEmpty,
}: LayoutSlotProps & { isEditing: boolean; isEmpty: boolean }) {
  const cfg = VARIANT_CONFIG[variant];
  const isRow = layoutDirection === "row";

  const slotClassName = [
    layoutClassName,
    cfg.slotLayoutClass,
    variant === "grid" && !isEmpty && "contents cms-grid-bridge",
    isEditing && variant !== "grid" && "cms-layout-slot",
    isEditing && variant === "grid" && isEmpty && "cms-layout-slot",
    isEditing && `cms-layout-slot--${variant}`,
    isEditing && isRow && "cms-layout-slot--row",
    isEmpty && "cms-layout-slot--empty",
    isEditing && !isEmpty && variant !== "grid" && "cms-layout-slot--filled",
    isEditing && cfg.editorPadding,
  ]
    .filter(Boolean)
    .join(" ");

  const blockClassName = [
    variant === "grid" && isEmpty
      ? "relative col-span-full w-full min-w-0 self-stretch"
      : cellClassName ??
        (variant === "column"
          ? "relative min-w-0 w-full self-stretch"
          : "relative w-full min-w-0 self-stretch"),
    isEditing && variant === "grid" && isEmpty && "cms-layout-block",
    isEditing && variant !== "grid" && "cms-layout-block",
    isEditing && variant === "grid" && isEmpty && "cms-layout-block--grid",
    isEditing && variant !== "grid" && `cms-layout-block--${variant}`,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      {variant === "grid" && !isEmpty ? (
        <Slot
          minEmptyHeight={isEditing ? cfg.minEmptyHeight : undefined}
          className={slotClassName || undefined}
          style={layoutStyle}
        />
      ) : (
        <div className={blockClassName}>
          <Slot
            minEmptyHeight={isEditing ? cfg.minEmptyHeight : undefined}
            className={slotClassName || undefined}
            style={layoutStyle}
          />
        </div>
      )}
    </>
  );
}
